import { Link } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { portalMoney, portalDate } from "@/lib/portalApi";

export const StatusChip = ({ children, tone = "stone" }) => (
  <span
    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
      tone === "green"
        ? "bg-emerald-50 text-emerald-700"
        : tone === "blue"
          ? "bg-teal-50 text-teal-700"
          : "bg-slate-100 text-slate-600"
    }`}
  >
    {children}
  </span>
);

const card =
  "group rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300";

export const ProjectCard = ({ pr }) => (
  <Link
    to={`/portal/projects/${pr.id}`}
    data-testid={`portal-project-card-${pr.id}`}
    className={card}
  >
    <div className="flex items-center gap-2">
      <span className="font-mono-data text-xs text-slate-500">{pr.code}</span>
      <StatusChip tone="blue">{pr.stage}</StatusChip>
      {pr.accepted_total ? (
        <span className="font-mono-data ml-auto text-base font-semibold text-slate-900">
          {portalMoney(pr.accepted_total)}
        </span>
      ) : null}
    </div>
    <div className="mt-2 text-sm font-semibold text-slate-900">{pr.name}</div>
    <div className="mt-1 text-xs text-slate-500">{pr.job_address}</div>
    <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-slate-900">
      {(pr.quotes || []).length} quote
      {(pr.quotes || []).length === 1 ? "" : "s"} · {(pr.jobs || []).length} job
      {(pr.jobs || []).length === 1 ? "" : "s"}
      {pr.target_install_date
        ? ` · install ${portalDate(pr.target_install_date)}`
        : ""}
    </div>
  </Link>
);

export const QuoteCard = ({ q }) => (
  <Link
    to={`/portal/quotes/${q.id}`}
    data-testid={`portal-quote-card-${q.id}`}
    className={card}
  >
    <div className="flex items-center gap-2">
      <span className="font-mono-data text-xs text-slate-500">{q.code}</span>
      {q.accepted_at ? (
        <StatusChip tone="green">Accepted</StatusChip>
      ) : (
        <StatusChip tone="blue">{q.status || "Sent"}</StatusChip>
      )}
      <span className="font-mono-data ml-auto text-base font-semibold text-slate-900">
        {q.total === null || q.total === undefined ? "" : portalMoney(q.total)}
      </span>
    </div>
    <div className="mt-2 text-sm font-semibold text-slate-900">
      {q.project_name}
    </div>
    <div className="mt-1 text-xs text-slate-500">
      {[q.slab_color, q.sqft ? `${q.sqft} ft²` : "", q.job_address]
        .filter(Boolean)
        .join(" · ")}
    </div>
    <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-slate-900">
      {portalDate(q.created_at)} · View quote
    </div>
  </Link>
);

export const InvoiceCard = ({ i }) => (
  <Link
    to={`/portal/invoices/${i.id}`}
    data-testid={`portal-invoice-card-${i.id}`}
    className={card}
  >
    <div className="flex items-center gap-2">
      <span className="font-mono-data text-xs text-slate-500">{i.code}</span>
      <StatusChip tone={i.status === "Paid" ? "green" : "blue"}>
        {i.status}
      </StatusChip>
      <span className="font-mono-data ml-auto text-base font-semibold text-slate-900">
        {portalMoney(i.balance)}
      </span>
    </div>
    <div className="mt-2 text-sm font-semibold text-slate-900">{i.title}</div>
    <div className="mt-1 text-xs text-slate-500">
      {[
        i.project_name,
        i.due_date ? `due ${portalDate(i.due_date)}` : "due on install",
      ]
        .filter(Boolean)
        .join(" · ")}
    </div>
    <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-slate-900">
      {i.status === "Paid" ? "View receipt" : "Open and pay"}
    </div>
  </Link>
);

export const JobCard = ({ j }) => (
  <Link
    to={`/portal/jobs/${j.id}`}
    data-testid={`portal-job-card-${j.id}`}
    className={card}
  >
    <div className="flex items-center gap-2">
      <span className="font-mono-data text-xs text-slate-500">{j.code}</span>
      <StatusChip>{j.stage}</StatusChip>
      {j.install_date && (
        <span className="ml-auto text-xs text-slate-500">
          Install {portalDate(j.install_date)}
        </span>
      )}
    </div>
    <div className="mt-2 text-sm font-semibold text-slate-900">
      {j.project_name}
    </div>
    <div className="mt-1 text-xs text-slate-500">{j.address}</div>
    <div className="mt-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-slate-900">
      <CheckCircle size={12} weight="bold" /> Follow this job
    </div>
  </Link>
);
